export const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    confirmed: 'bg-green-100 text-green-800 border-green-300',
    completed: 'bg-blue-100 text-blue-800 border-blue-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300'
  };
  return colors[status] || colors.pending;
};

export const formatServiceM8Text = (booking) => {
  let fullAddress = booking.address || '';
  if (!fullAddress && booking.farm_details) {
    const addressParts = [
      booking.farm_details.address_number,
      booking.farm_details.address_road,
      booking.farm_details.address_town,
      booking.farm_details.address_region,
      booking.farm_details.address_postcode
    ].filter(Boolean);
    fullAddress = addressParts.length > 0 ? addressParts.join(', ') : (booking.farm_details.site_address || 'Not provided');
  }

  const farmName = booking.farm_name || booking.farm_details?.farm_name || 'Unknown Farm';
  const contactName = booking.contact_name || booking.farm_details?.farm_contact || '';
  const phone = booking.phone || booking.farm_details?.farm_phone || '';
  const participantCode = booking.participant_code || booking.farm_details?.participant_code || '';
  const dairyNumber = booking.dairy_number || booking.farm_details?.dairy_number || '';
  const preferredDate = booking.preferred_date || booking.farm_details?.preferred_date || 'Not specified';
  const preferredTime = booking.preferred_time || booking.farm_details?.preferred_time || 'Not specified';

  const servicesList = booking.services
    ?.map(s => {
      const numAnimals = s.num_animals || s.quantity || 0;
      const unitPrice = s.price_per_head || s.unit_price || 0;
      const lineTotal = s.cost || s.total_price || (numAnimals * unitPrice);
      return `  • ${s.service_name}
    Animals: ${numAnimals}
    P/Head:  $${parseFloat(unitPrice).toFixed(2)}
    Total:   $${parseFloat(lineTotal).toFixed(2)}`;
    })
    .join('\n\n') || '  No services listed';

  const establishmentFee = booking.establishment_fee ?? 120.00;
  const servicesSubtotal = booking.services?.reduce((sum, s) => {
    const cost = s.cost || s.total_price || 0;
    return sum + parseFloat(cost);
  }, 0) || 0;

  const subtotal = booking.total_cost_excl_gst?.toFixed(2) || '0.00';
  const gst = booking.gst_amount?.toFixed(2) || '0.00';
  const total = booking.total_cost_incl_gst?.toFixed(2) || '0.00';

  return `MEASURED LIVESTOCK - SERVICE REQUEST
═══════════════════════════════════════════════════════════════

FARM DETAILS:
─────────────────────────────────────────────────────────────
Farm Name:        ${farmName}
Contact:          ${contactName}
Phone:            ${phone || 'Not provided'}
${participantCode ? `PTPT/NAIT:        ${participantCode}\n` : ''}${dairyNumber ? `Dairy Number:     ${dairyNumber}\n` : ''}
SERVICE ADDRESS:
─────────────────────────────────────────────────────────────
${fullAddress || 'Not provided'}

PREFERRED DATE/TIME:
─────────────────────────────────────────────────────────────
Date:             ${preferredDate}
Time:             ${preferredTime}

SERVICES REQUESTED:
─────────────────────────────────────────────────────────────
${servicesList}

PRICING BREAKDOWN:
─────────────────────────────────────────────────────────────
Services Total:           $${servicesSubtotal.toFixed(2)}
Establishment Fee:        $${parseFloat(establishmentFee).toFixed(2)}
─────────────────────────────────────────────────────────────
Subtotal (excl. GST):     $${subtotal}
GST (15%):                $${gst}
─────────────────────────────────────────────────────────────
TOTAL (incl. GST):        $${total}

═══════════════════════════════════════════════════════════════`;
};

export const openCorporateQuoteWindow = (sub) => {
  const printWindow = window.open('', '_blank');

  const farmName = sub.farm_name || sub.farm_details?.farm_name || 'Unknown Farm';
  const contactName = sub.contact_name || sub.farm_details?.farm_contact || '';
  const email = sub.email || sub.farm_details?.farm_email || '';
  const phone = sub.phone || sub.farm_details?.farm_phone || '';
  const participantCode = sub.participant_code || sub.farm_details?.participant_code || '';
  const dairyNumber = sub.dairy_number || sub.farm_details?.dairy_number || '';

  let address = sub.address || '';
  if (!address && sub.farm_details) {
    const addressParts = [
      sub.farm_details.address_number,
      sub.farm_details.address_road,
      sub.farm_details.address_town,
      sub.farm_details.address_region,
      sub.farm_details.address_postcode
    ].filter(Boolean);
    address = addressParts.length > 0 ? addressParts.join(', ') : (sub.farm_details.site_address || '');
  }

  const preferredDate = sub.preferred_date || sub.farm_details?.preferred_date || '';
  const preferredTime = sub.preferred_time || sub.farm_details?.preferred_time || '';
  const notes = sub.notes || sub.farm_details?.notes || '';
  const corporateCompany = sub.corporate_company || sub.company_name || '';
  const corporateUser = sub.corporate_user || sub.staff_name || '';
  const corporateEmail = sub.corporate_email || sub.staff_email || '';

  const servicesHTML = sub.services?.map(s => `
    <tr>
      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${s.service_name}</td>
      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${s.num_animals}</td>
      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 500;">$${s.cost?.toFixed(2) || '0.00'}</td>
    </tr>
  `).join('') || '';

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Quote - ${farmName}</title>
      <style>
        @page { margin: 20mm; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; color: #1f2937; font-size: 14px; line-height: 1.5; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #1a4d2e; }
        .logo { font-size: 20px; font-weight: bold; color: #1a4d2e; }
        .logo span { color: #6b7280; font-weight: normal; }
        .quote-badge { background: #1a4d2e; color: white; padding: 8px 16px; font-size: 12px; font-weight: 600; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
        .info-box { background: #f9fafb; padding: 15px; border-radius: 6px; }
        .info-box-title { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
        .info-box p { margin: 4px 0; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th { background: #1a4d2e; color: white; padding: 12px; text-align: left; font-weight: 600; }
        th:last-child { text-align: right; }
        th:nth-child(2) { text-align: center; }
        .totals { margin-top: 20px; margin-left: auto; width: 280px; }
        .totals-row { display: flex; justify-content: space-between; padding: 8px 12px; font-size: 14px; }
        .totals-row.total { background: #1a4d2e; color: white; font-weight: bold; font-size: 16px; border-radius: 4px; margin-top: 8px; }
        .status { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-left: 10px; }
        .status-pending { background: #fef3c7; color: #92400e; }
        .status-accepted { background: #dcfce7; color: #166534; }
        .status-rejected { background: #fee2e2; color: #991b1b; }
        .status-draft { background: #fef3c7; color: #92400e; }
        .footer { margin-top: 40px; text-align: center; color: #9ca3af; font-size: 11px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">MEASURED <span>LIVESTOCK</span></div>
        <div class="quote-badge">SERVICE QUOTE</div>
      </div>
      
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
        <div>
          <span style="color: #6b7280; font-size: 12px;">Quote Reference:</span>
          <span style="font-weight: 600; margin-left: 5px;">${sub.id?.slice(0, 8).toUpperCase() || 'N/A'}</span>
          <span class="status status-${sub.status || 'draft'}">
            ${sub.status === 'accepted' ? '✓ ACCEPTED' : sub.status === 'rejected' ? '✗ REJECTED' : sub.status === 'pending' ? '⏳ PENDING' : '📝 DRAFT'}
          </span>
        </div>
        <div style="color: #6b7280; font-size: 12px;">
          Date: ${sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}
        </div>
      </div>
      
      <div class="info-grid">
        <div class="info-box">
          <div class="info-box-title">Customer Details</div>
          <p><strong>${farmName}</strong></p>
          <p>${contactName}</p>
          <p>${email}</p>
          ${phone ? `<p>${phone}</p>` : ''}
          ${participantCode ? `<p><strong>PTPT/NAIT:</strong> ${participantCode}</p>` : ''}
          ${dairyNumber ? `<p><strong>Dairy #:</strong> ${dairyNumber}</p>` : ''}
        </div>
        <div class="info-box">
          <div class="info-box-title">Service Location</div>
          ${address ? `<p>${address}</p>` : '<p style="color: #9ca3af;">Address not provided</p>'}
          ${preferredDate ? `<p style="margin-top: 10px;"><strong>Preferred Date:</strong> ${preferredDate}</p>` : ''}
          ${preferredTime ? `<p><strong>Preferred Time:</strong> ${preferredTime}</p>` : ''}
        </div>
      </div>
      
      ${corporateCompany ? `
      <div class="info-box" style="margin-bottom: 20px;">
        <div class="info-box-title">Submitted By</div>
        <p><strong>${corporateCompany}</strong></p>
        <p>${corporateUser}${corporateEmail ? ` (${corporateEmail})` : ''}</p>
      </div>
      ` : ''}
      
      <table>
        <thead>
          <tr>
            <th>Service Description</th>
            <th style="text-align: center;">Animals</th>
            <th style="text-align: right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${servicesHTML}
          <tr>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">Establishment Fee</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">-</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 500;">$120.00</td>
          </tr>
        </tbody>
      </table>
      
      <div class="totals">
        <div class="totals-row" style="background: #f9fafb; border-radius: 4px;">
          <span>Subtotal</span>
          <span>$${sub.total_cost_excl_gst?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="totals-row" style="color: #6b7280;">
          <span>GST (15%)</span>
          <span>$${sub.gst_amount?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="totals-row total">
          <span>Total (NZD)</span>
          <span>$${sub.total_cost_incl_gst?.toFixed(2) || '0.00'}</span>
        </div>
      </div>
      
      ${notes ? `
        <div style="background: #fffbeb; border-left: 3px solid #f59e0b; padding: 12px 15px; margin-top: 20px; font-size: 13px;">
          <strong>Notes:</strong> ${notes}
        </div>
      ` : ''}
      
      <div class="footer">
        <p>Measured Livestock • Professional Livestock Services</p>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};
